<template>
  <div class="approval-list-filter">
    <b-row class="mb-5">
      <b-col>
        <div class="search-col">
          <date-picker
            placeholder="Từ ngày"
            class="form-control-sm"
            :config="dpConfigs"
            v-model="filterData.startDate"
          ></date-picker>
          <span class="ml-1 mr-1"></span>
          <date-picker
            placeholder="Đến ngày"
            class="form-control-sm"
            :config="dpConfigs"
            v-model="filterData.endDate"
          ></date-picker>
        </div>
      </b-col>
      <b-col>
        <b-form-select
          size="sm"
          v-model="filterData.type"
          :options="typeOptions"
          value-field="id"
          text-field="name"
          class="select-style"
        ></b-form-select>
      </b-col>
      <b-col>
        <b-form-select
          size="sm"
          v-model="filterData.status"
          :options="statusOptions"
          value-field="id"
          text-field="name"
          class="select-style"
        ></b-form-select>
      </b-col>
      <b-col>
        <Autosuggest
          class="border-radius-none"
          :model="filterData.storeText"
          :suggestions="filteredStores"
          placeholder="cửa hàng"
          :limit="200"
          @select="onSelectedStore"
          @change="onInputChangeStore"
          suggestionName="suggestionName"
          :disabled="disableStore"
        />
      </b-col>
    </b-row>

    <b-row class="mb-5">
      <b-col md="3">
        <b-form-input
          v-model="filterData.code"
          type="text"
          placeholder="Mã phiếu"
          size="sm"
          v-on:keyup.enter="onFilter()"
        ></b-form-input>
      </b-col>
      <b-col md="3">
        <b-form-input
          v-model="filterData.entityId"
          type="text"
          placeholder="ID chứng từ"
          size="sm"
          v-on:keyup.enter="onFilter()"
        ></b-form-input>
      </b-col>
      <b-col md="3">
        <Autosuggest
          class="border-radius-none"
          :model="filterData.employeeText"
          :suggestions="filteredEmployees"
          placeholder="người tạo"
          :limit="200"
          @select="onSelectedEmployee"
          @change="onSearchEmployeeChange"
          suggestionName="suggestionName"
        />
      </b-col>
      <b-col md="3">
        <b-form-select
          size="sm"
          v-model="filterData.paymentType"
          :options="paymentTypeOptions"
          value-field="id"
          text-field="name"
          class="select-style"
        ></b-form-select>
      </b-col>
    </b-row>

    <b-row class="mb-5">
      <b-col md="1">
        <b-button
          class="font-weight-bold"
          variant="primary"
          size="sm"
          @click="onFilter"
        >Lọc</b-button>
      </b-col>
    </b-row>
  </div>
</template>

<script>
import datePicker from 'vue-bootstrap-datetimepicker';
import Autosuggest from '@/view/base/auto-sugguest/AutoSuggest.vue';
import timeUtils from '@/utils/date';
import moment from 'moment';
import { xoa_dau } from '@/utils/common';

export default {
  name: 'ApprovalListFilter',
  components: {
    datePicker,
    Autosuggest,
  },
  props: {
    typeOptions: {
      type: Array,
      required: true,
    },
    statusOptions: {
      type: Array,
      required: true,
    },
    paymentTypeOptions: {
      type: Array,
      required: true,
    },
    initialFilter: {
      type: Object,
      default: () => ({}),
    },
  },
  data() {
    return {
      filterData: {
        startDate: '',
        endDate: '',
        type: null,
        status: null,
        storeId: null,
        storeText: '',
        code: '',
        entityId: '',
        employeeId: null,
        employeeText: '',
        paymentType: null,
      },
      dpConfigs: timeUtils.DP_CONFIG.date,
      stores: [],
      filteredStores: [],
      employees: [],
      filteredEmployees: [],
      disableStore: false,
      limit: 200,
    };
  },
  created() {
    // Initialize filter with any provided initial values
    this.filterData = { ...this.filterData, ...this.initialFilter };
  },
  methods: {
    /**
     * Emit filter event with current filter data
     */
    onFilter() {
      const formattedFilter = this.formatFilterData();
      this.$emit('filter', formattedFilter);
    },

    /**
     * Format filter data for API request
     */
    formatFilterData() {
      return {
        page: 1,
        limit: 10,
        code: this.filterData.code ? this.filterData.code.trim() : '',
        entityId: this.filterData.entityId
          ? this.filterData.entityId.trim()
          : '',
        status: this.filterData.status,
        fromDate: this.filterData.startDate
          ? moment(this.filterData.startDate, 'DD/MM/YYYY').format('yyyy-MM-DD')
          : '',
        toDate: this.filterData.endDate
          ? moment(this.filterData.endDate, 'DD/MM/YYYY').format('yyyy-MM-DD')
          : '',
        storeId: this.filterData.storeId,
        type: this.filterData.type,
        paymentType: this.filterData.paymentType,
        employeeId: this.filterData.employeeId,
      };
    },

    /**
     * Set stores data and initialize filtered stores
     */
    setStores(stores) {
      if (stores.length === 1) {
        this.filterData.storeId = stores[0].id;
        this.filterData.storeText = stores[0].name;
        this.disableStore = true;
      } else {
        this.stores = stores.map((store) => ({
          id: store.id,
          name: store.name,
          shortName: store.shortName,
          suggestionName: store.shortName + ' ( ' + store.name + ')',
        }));
        this.filteredStores = [...this.stores];
      }
    },

    /**
     * Set employees data and initialize filtered employees
     */
    setEmployees(employees) {
      this.employees = employees.map((employee) => {
        let suggestionName = employee.name;
        if (employee.code) {
          suggestionName += ' ( ' + employee.code + ')';
        }
        const nameNoneSign = xoa_dau(employee.name);
        return {
          id: employee.id,
          name: employee.name,
          code: employee.code,
          suggestionName: suggestionName,
          nameNoneSign: nameNoneSign,
        };
      });
      this.filteredEmployees = [...this.employees];
    },

    /**
     * Handle store selection
     */
    onSelectedStore(option) {
      this.filterData.storeId = option.item.id;
      this.filterData.storeText = option.item.name;
    },

    /**
     * Handle store input change
     */
    onInputChangeStore(text) {
      if (!text) {
        text = '';
      }
      this.filterData.storeText = text;
      this.filterData.storeId = null;

      const filteredData = this.stores
        .filter((item) => {
          return (
            item.name.toLowerCase().indexOf(text.toLowerCase()) > -1 ||
            item.shortName.toLowerCase().indexOf(text.toLowerCase()) > -1
          );
        })
        .slice(0, this.limit);

      this.filteredStores = [...filteredData];
    },

    /**
     * Handle employee selection
     */
    onSelectedEmployee(option) {
      this.filterData.employeeId = option.item.id;
      this.filterData.employeeText = option.item.name;
    },

    /**
     * Handle employee input change
     */
    onSearchEmployeeChange(text) {
      if (!text) {
        text = '';
      }
      this.filterData.employeeText = text;
      this.filterData.employeeId = null;

      const filteredData = this.employees
        .filter((item) => {
          return (
            item.nameNoneSign.toLowerCase().indexOf(text.toLowerCase()) > -1 ||
            item.name.toLowerCase().indexOf(text.toLowerCase()) > -1 ||
            item.code.toLowerCase().indexOf(text.toLowerCase()) > -1
          );
        })
        .slice(0, this.limit);

      this.filteredEmployees = [...filteredData];
    },

    /**
     * Reset filter to default values
     */
    resetFilter() {
      this.filterData = {
        startDate: '',
        endDate: '',
        type: null,
        status: null,
        storeId: null,
        storeText: '',
        code: '',
        entityId: '',
        employeeId: null,
        employeeText: '',
        paymentType: null,
      };

      // If there's only one store, set it as the selected store
      if (this.stores.length === 1) {
        this.filterData.storeId = this.stores[0].id;
        this.filterData.storeText = this.stores[0].name;
      }
    },
  },
};
</script>

<style lang="scss" scoped>
.approval-list-filter {
  .search-col {
    display: flex;
    align-items: center;
  }

  .select-style {
    width: 100%;
  }
}
</style>
